/**
 * 入力値の検証ルール（DOM に依存しない純粋な関数）。
 *
 * サーバ側の CI4 のルール（app/Libraries/Validation/InputRules.php と CI4 標準の required / max_length / valid_email）と
 * 同じ判定にする。最終的な判定はサーバ側で行うため、ここは入力中に早く知らせるためのもの。
 * 空の値は required 以外のルールでは妥当とする（必須かどうかは required で判定する）。
 */
export class InputRule {
    /**
     * 空でないか（前後の空白は除いて判定する。サーバ側も trim してから検証する）
     */
    public static required(value: string): boolean {
        return value.trim() !== "";
    }

    /**
     * 文字数が上限以下か（CI4 の max_length と同じく、バイトではなく文字で数える）
     */
    public static maxLength(value: string, max: number): boolean {
        return [...value].length <= max;
    }

    /**
     * メールアドレスの形か（サーバ側は filter_var で厳密に判定する）
     */
    public static validEmail(value: string): boolean {
        if (value === "") {
            return true;
        }

        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    /**
     * 生年月日として妥当か（Y-m-d、1900-01-01 から今日まで）
     * @param today 今日の日付（テストで固定できるように引数にする）
     */
    public static validBirthday(value: string, today: Date = new Date()): boolean {
        if (value === "") {
            return true;
        }

        const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
        if (matched === null) {
            return false;
        }

        const year = Number(matched[1]);
        const month = Number(matched[2]);
        const day = Number(matched[3]);
        const date = new Date(year, month - 1, day);
        // 2 月 30 日などの存在しない日付を除く
        if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
            return false;
        }

        const end_of_today = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        return date >= new Date(1900, 0, 1) && date <= end_of_today;
    }

    /**
     * 電話番号として妥当か（数字・ハイフン・括弧・+・空白で、数字が 10〜15 桁）
     */
    public static validTel(value: string): boolean {
        if (value === "") {
            return true;
        }

        if (!/^[0-9+\-() ]+$/.test(value)) {
            return false;
        }

        const digits = value.replace(/\D/g, "");

        return digits.length >= 10 && digits.length <= 15;
    }

    /**
     * 改行・タブ以外の制御文字を含まないか
     */
    public static noControlChars(value: string): boolean {
        return !/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/.test(value);
    }

    /**
     * 1 行の値に改行・制御文字を含まないか
     */
    public static singleLine(value: string): boolean {
        return !/[\x00-\x1F\x7F]/.test(value);
    }
}
